import { useState } from "react";
import { Grid as AntdMobileGrid } from "antd-mobile";
import { BaseGridProps, GridItem } from "./Grid.types";
import "./Grid.scss";

// 表格组件Props类型
export interface GridTableProps {
  headers: GridItem[];
  rows: GridItem[][];
  columns?: number;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  gap?: number;
  onRowClick?: (rowIndex: number, rowItems: GridItem[]) => void;
}

// 表格组件，内部管理所有行
export const GridTable = ({
  headers,
  rows,
  columns = 4,
  size = "md",
  gap = 0,
  onRowClick,
}: GridTableProps) => {
  const [activeRowIndex, setActiveRowIndex] = useState<number | undefined>(
    undefined
  );

  const handleRowClick = (rowIndex: number, rowItems: GridItem[]) => {
    setActiveRowIndex(rowIndex);
    if (onRowClick) {
      onRowClick(rowIndex, rowItems);
    }
  };

  return (
    <div className="grid-table-container">
      {/* 表头 */}
      <Grid
        columns={columns}
        size={size}
        gap={gap}
        tableType="header"
        items={headers}
      />

      {/* 表格行 */}
      {rows.map((rowItems, rowIndex) => {
        const isActive = activeRowIndex === rowIndex;

        return (
          <div
            key={`row-${rowIndex}`}
            onClick={() => handleRowClick(rowIndex, rowItems)}
            style={{ cursor: "pointer" }}
          >
            <Grid
              columns={columns}
              size={size}
              gap={gap}
              className={`grid--table grid--table-row ${isActive ? "grid--table-row--active" : ""}`}
              items={rowItems}
            />
          </div>
        );
      })}
    </div>
  );
};

interface GridComponent extends React.FC<BaseGridProps> {
  Item: typeof AntdMobileGrid.Item;
  Table: typeof GridTable;
}

const Grid: GridComponent = Object.assign(
  ({
    children,
    variant = "primary",
    size = "md",
    items = [],
    columns = 4,
    gap = 16,
    className = "",
    tableType,
    testId,
    ...props
  }: BaseGridProps) => {
    // 构建className
    const classes = ["grid", `grid--${variant}`, `grid--${size}`];

    // 如果是表格类型，添加表格相关的class
    if (tableType === "header") {
      classes.push("grid--table");
      classes.push("grid--table-header");
    }

    // 添加用户自定义的className
    if (className) {
      classes.push(className);
    }

    const gridClass = classes.filter(Boolean).join(" ");

    return (
      <AntdMobileGrid
        className={gridClass}
        columns={columns}
        gap={gap}
        data-testid={testId}
        {...props}
      >
        {items.length > 0
          ? items.map(item => (
              <AntdMobileGrid.Item
                key={item.key}
                onClick={item.onClick}
                span={item.span}
              >
                <div
                  className={`grid__item ${item.disabled ? "grid__item--disabled" : ""}`}
                >
                  {item.icon && <div className="grid__icon">{item.icon}</div>}
                  {item.text && <div className="grid__text">{item.text}</div>}
                </div>
              </AntdMobileGrid.Item>
            ))
          : children}
      </AntdMobileGrid>
    );
  },
  {
    Item: AntdMobileGrid.Item,
    Table: GridTable,
  }
);

export { Grid };
export default Grid;
